import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Modal from './Modal';
import { SkeletonTable } from './Skeleton';
import EmptyState, { EmptyStateNoData } from './EmptyState';
import Pagination from './Pagination';
import { usePagination } from '../hooks/usePagination';

interface DevelopmentPieceFile {
  id: number;
  development_piece_id: number;
  version: number;
  drive_file_name: string;
  drive_file_url: string;
  drive_file_id?: string;
  drive_folder: 'DISEÑO' | 'FINALES';
  uploaded_at: string;
  uploaded_by?: number;
  description?: string;
}

interface DevelopmentPiece {
  id: number;
  piece_code: string;
  piece_type: 'P' | 'A' | 'C' | 'N';
  name: string;
  description?: string;
  status: 'SIN_ENVIAR' | 'ENVIADO' | 'REVISION' | 'PRODUCCION' | 'DESCARTADO';
  priority: 'BAJA' | 'MEDIA' | 'ALTA';
  foundry_supplier?: string;
  approval_checklist: Array<{ task: string; completed: boolean }>;
  created_by?: number;
  sent_to_foundry_at?: string;
  foundry_notes?: string;
  revision_notes?: string;
  created_at: string;
  updated_at: string;
  files: DevelopmentPieceFile[];
  created_by_username?: string;
}

const PRIORITY_LABELS = {
  BAJA: 'Baja',
  MEDIA: 'Media',
  ALTA: 'Alta',
};

const PRIORITY_COLORS = {
  BAJA: 'var(--text-secondary)',
  MEDIA: 'var(--warning)',
  ALTA: 'var(--danger)',
};

const STATUS_LABELS: Record<DevelopmentPiece['status'], string> = {
  SIN_ENVIAR: 'Sin Enviar',
  ENVIADO: 'Enviado',
  REVISION: 'Revisión',
  PRODUCCION: 'Producción',
  DESCARTADO: 'Descartado',
};

const STATUS_COLORS: Record<DevelopmentPiece['status'], string> = {
  SIN_ENVIAR: 'var(--text-secondary)',
  ENVIADO: 'var(--info)',
  REVISION: 'var(--warning)',
  PRODUCCION: 'var(--success)',
  DESCARTADO: 'var(--danger)',
};

const PIECE_TYPE_LABELS: Record<DevelopmentPiece['piece_type'], string> = {
  P: 'Pulsera',
  A: 'Anillo',
  C: 'Colgante',
  N: 'Necklace',
};

export default function DevelopmentPieces() {
  const { api } = useApi();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [pieces, setPieces] = useState<DevelopmentPiece[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<DevelopmentPiece['status'] | 'ALL'>('ALL');

  // Filtrar piezas
  const filteredPieces = pieces.filter((piece) => {
    if (statusFilter !== 'ALL' && piece.status !== statusFilter) {
      return false;
    }
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      piece.piece_code.toLowerCase().includes(search) ||
      piece.name.toLowerCase().includes(search) ||
      PIECE_TYPE_LABELS[piece.piece_type].toLowerCase().includes(search)
    );
  });

  // Paginación
  const {
    currentPage,
    totalPages,
    paginatedItems,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
  } = usePagination(filteredPieces, {
    initialItemsPerPage: 25,
  });

  // Estados de modales
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isMoveToProductionModalOpen, setIsMoveToProductionModalOpen] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState<DevelopmentPiece | null>(null);
  const [editingPiece, setEditingPiece] = useState<DevelopmentPiece | null>(null);
  const [pieceType, setPieceType] = useState<'P' | 'A' | 'C' | 'N'>('A');
  const [suggestedCode, setSuggestedCode] = useState<string | null>(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [googleDriveAuthStatus, setGoogleDriveAuthStatus] = useState<{
    authenticated: boolean;
    connectedAt?: string;
    lastUpdated?: string;
  } | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [formData, setFormData] = useState({
    piece_code: '',
    name: '',
    description: '',
    status: 'SIN_ENVIAR' as DevelopmentPiece['status'],
    priority: 'MEDIA' as DevelopmentPiece['priority'],
    foundry_supplier: '',
  });
  const [moveToProductionData, setMoveToProductionData] = useState({
    material_base: 'PLATA' as 'PLATA' | 'LATON',
    peso_plata: '',
    peso_laton: '',
    coste_fundicion: '',
    coste_pvd: '',
    coste_dlc: '',
  });

  useEffect(() => {
    loadPieces();
    checkGoogleDriveAuth();
  }, [user]);

  const checkGoogleDriveAuth = async () => {
    // Si el usuario ya está autenticado con Google (tiene google_id), 
    // consideramos que está conectado a Drive automáticamente.
    if (user?.google_id) {
      setGoogleDriveAuthStatus({
        authenticated: true,
        // Podríamos poner el email de Google aquí si lo tenemos en el objeto user
      });
      setCheckingAuth(false);
      return;
    }

    try {
      setCheckingAuth(true);
      const response = await api.get('/google-drive-auth/status');
      setGoogleDriveAuthStatus(response.data);
    } catch (err: any) {
      setGoogleDriveAuthStatus({ authenticated: false });
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleConnectGoogleDrive = async () => {
    try {
      const response = await api.get('/google-drive-auth/auth-url');
      const authUrl = response.data.authUrl;

      // Abrir en popup
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      window.open(
        authUrl,
        'Google Drive Auth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al iniciar autenticación');
    }
  };

  const handleDisconnectGoogleDrive = async () => {
    if (!confirm('¿Estás seguro de que quieres desconectar Google Drive?')) {
      return;
    }

    try {
      await api.post('/google-drive-auth/disconnect');
      setGoogleDriveAuthStatus({ authenticated: false });
      showSuccess('Google Drive desconectado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al desconectar');
    }
  };

  const loadPieces = async () => {
    try {
      setLoading(true);
      const response = await api.get('/development-pieces');
      setPieces(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar piezas en desarrollo');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedCode = async (type: 'P' | 'A' | 'C' | 'N', material: 'PLATA' | 'LATON' = 'PLATA') => {
    if (editingPiece) return;

    try {
      setLoadingSuggestion(true);
      const response = await api.get('/development-pieces/suggested-code', {
        params: { piece_type: type, material },
      });

      if (response.data?.next_code) {
        setSuggestedCode(response.data.next_code);
        if (!formData.piece_code) {
          setFormData((prev) => ({
            ...prev,
            piece_code: response.data.next_code,
          }));
        }
      }
    } catch (err: any) {
      console.error('Error obteniendo código sugerido:', err);
      showError(err.response?.data?.error || 'No se pudo obtener el siguiente código sugerido');
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleOpenModal = (piece?: DevelopmentPiece) => {
    if (piece) {
      setEditingPiece(piece);
      setFormData({
        piece_code: piece.piece_code,
        name: piece.name,
        description: piece.description || '',
        status: piece.status,
        priority: piece.priority || 'MEDIA',
        foundry_supplier: piece.foundry_supplier || '',
      });
      setPieceType(piece.piece_type);
      setSuggestedCode(null);
    } else {
      setEditingPiece(null);
      setFormData({
        piece_code: '',
        name: '',
        description: '',
        status: 'SIN_ENVIAR',
        priority: 'MEDIA',
        foundry_supplier: '',
      });
      setPieceType('A');
      setSuggestedCode(null);
      void fetchSuggestedCode('A', 'PLATA');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPiece(null);
    setSuggestedCode(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        piece_code: formData.piece_code,
        piece_type: pieceType,
        name: formData.name,
        description: formData.description || undefined,
        status: editingPiece ? formData.status : undefined,
        priority: formData.priority,
        foundry_supplier: formData.foundry_supplier || undefined,
      };

      if (editingPiece) {
        await api.put(`/development-pieces/${editingPiece.id}`, data);
        showSuccess('Pieza actualizada correctamente');
      } else {
        await api.post('/development-pieces', data);
        showSuccess('Pieza creada correctamente');
      }

      handleCloseModal();
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al guardar pieza');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta pieza? Solo se pueden eliminar piezas descartadas.')) {
      return;
    }

    try {
      await api.delete(`/development-pieces/${id}`);
      showSuccess('Pieza eliminada correctamente');
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar pieza');
    }
  };

  const handleOpenDetail = (piece: DevelopmentPiece) => {
    setSelectedPiece(piece);
    setIsDetailModalOpen(true);
  };

  const handleOpenUpload = (piece: DevelopmentPiece) => {
    setSelectedPiece(piece);
    setUploadFile(null);
    setUploadDescription('');
    setIsUploadModalOpen(true);
  };

  const handleUploadFile = async () => {
    if (!selectedPiece || !uploadFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }

      await api.post(`/development-pieces/${selectedPiece.id}/upload-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSuccess('Archivo subido correctamente');
      setIsUploadModalOpen(false);
      setUploadFile(null);
      setUploadDescription('');
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al subir archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleMarkAsSentToFoundry = async (id: number) => {
    const notes = prompt('Notas para el fundidor (opcional):');
    if (notes === null) return; // Usuario canceló

    try {
      await api.post(`/development-pieces/${id}/mark-sent-to-foundry`, { notes: notes || undefined });
      showSuccess('Pieza marcada como enviada a fundidor');
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al marcar como enviado');
    }
  };

  const handleMarkAsInRevision = async (id: number) => {
    const notes = prompt('Notas de revisión (opcional):');
    if (notes === null) return;

    try {
      await api.post(`/development-pieces/${id}/mark-in-revision`, { notes: notes || undefined });
      showSuccess('Pieza marcada como en revisión');
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al marcar como en revisión');
    }
  };

  const handleOpenMoveToProduction = (piece: DevelopmentPiece) => {
    setSelectedPiece(piece);
    setMoveToProductionData({
      material_base: 'PLATA',
      peso_plata: '',
      peso_laton: '',
      coste_fundicion: '',
      coste_pvd: '',
      coste_dlc: '',
    });
    setIsMoveToProductionModalOpen(true);
  };

  const handleMoveToProduction = async () => {
    if (!selectedPiece) return;

    try {
      const data: any = {};
      if (moveToProductionData.material_base || moveToProductionData.peso_plata || moveToProductionData.peso_laton) {
        data.metal_piece_data = {
          material_base: moveToProductionData.material_base,
          peso_plata: moveToProductionData.peso_plata ? parseFloat(moveToProductionData.peso_plata) : undefined,
          peso_laton: moveToProductionData.peso_laton ? parseFloat(moveToProductionData.peso_laton) : undefined,
          coste_fundicion: moveToProductionData.coste_fundicion ? parseFloat(moveToProductionData.coste_fundicion) : undefined,
          coste_pvd: moveToProductionData.coste_pvd ? parseFloat(moveToProductionData.coste_pvd) : undefined,
          coste_dlc: moveToProductionData.coste_dlc ? parseFloat(moveToProductionData.coste_dlc) : undefined,
        };
      }

      await api.post(`/development-pieces/${selectedPiece.id}/move-to-production`, data);
      showSuccess('Pieza movida a producción correctamente');
      setIsMoveToProductionModalOpen(false);
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al mover a producción');
    }
  };

  const handleDiscard = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres descartar esta pieza?')) {
      return;
    }

    try {
      await api.post(`/development-pieces/${id}/discard`);
      showSuccess('Pieza descartada correctamente');
      loadPieces();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descartar pieza');
    }
  };

  if (loading) {
    return <SkeletonTable rows={10} columns={8} />;
  }

  // Renderizado principal
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1>Piezas en Desarrollo</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* Estado de autenticación Google Drive */}
          {!checkingAuth && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {googleDriveAuthStatus?.authenticated ? (
                <span style={{
                  color: 'var(--success)',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  ✅ Google Drive {user?.google_id ? 'Auto-' : ''}conectado
                </span>
              ) : (
                <button
                  className="btn btn-small btn-primary"
                  onClick={handleConnectGoogleDrive}
                  title="Conectar Google Drive para subir archivos"
                >
                  🔗 Conectar Google Drive
                </button>
              )}
            </div>
          )}
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            + Nueva Pieza
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div className="input-group" style={{ flex: '1', minWidth: '200px' }}>
          <input
            type="text"
            className="input"
            placeholder="Buscar por código, nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="input-group" style={{ minWidth: '150px' }}>
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as DevelopmentPiece['status'] | 'ALL')}
          >
            <option value="ALL">Todos los estados</option>
            <option value="SIN_ENVIAR">Sin Enviar</option>
            <option value="ENVIADO">Enviado</option>
            <option value="REVISION">Revisión</option>
            <option value="PRODUCCION">Producción</option>
            <option value="DESCARTADO">Descartado</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      {filteredPieces.length === 0 ? (
        <EmptyState
          icon="📦"
          title="No hay piezas"
          message="No se han encontrado piezas en desarrollo con los filtros actuales."
          action={{
            label: 'Crear nueva pieza',
            onClick: () => handleOpenModal(),
          }}
        />
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Tipo</th>
                  <th>Nombre</th>
                  <th>Prioridad</th>
                  <th>Estado</th>
                  <th>Versiones</th>
                  <th>Última Actualización</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((piece) => (
                  <tr key={piece.id}>
                    <td>
                      <strong>{piece.piece_code}</strong>
                    </td>
                    <td>{PIECE_TYPE_LABELS[piece.piece_type]}</td>
                    <td>{piece.name}</td>
                    <td>
                      <span
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: PRIORITY_COLORS[piece.priority],
                          backgroundColor: `${PRIORITY_COLORS[piece.priority]}20`,
                        }}
                      >
                        {PRIORITY_LABELS[piece.priority]}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: STATUS_COLORS[piece.status],
                          backgroundColor: `${STATUS_COLORS[piece.status]}20`,
                        }}
                      >
                        {STATUS_LABELS[piece.status]}
                      </span>
                    </td>
                    <td>{piece.files.length} archivo{piece.files.length !== 1 ? 's' : ''}</td>
                    <td>{new Date(piece.updated_at).toLocaleDateString('es-ES')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleOpenDetail(piece)}
                          title="Ver detalles"
                        >
                          👁️
                        </button>
                        {piece.status !== 'DESCARTADO' && (
                          <>
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => handleOpenUpload(piece)}
                              title="Subir archivo"
                            >
                              📤
                            </button>
                            {piece.status === 'SIN_ENVIAR' && (
                              <button
                                className="btn btn-small btn-info"
                                onClick={() => handleMarkAsSentToFoundry(piece.id)}
                                title="Marcar como enviado"
                              >
                                ✉️
                              </button>
                            )}
                            {piece.status === 'ENVIADO' && (
                              <button
                                className="btn btn-small btn-warning"
                                onClick={() => handleMarkAsInRevision(piece.id)}
                                title="Marcar como en revisión"
                              >
                                🔍
                              </button>
                            )}
                            {piece.status === 'REVISION' && (
                              <button
                                className="btn btn-small btn-success"
                                onClick={() => handleOpenMoveToProduction(piece)}
                                title="Mover a producción"
                              >
                                ✅
                              </button>
                            )}
                            {piece.status !== 'PRODUCCION' && (
                              <button
                                className="btn btn-small btn-danger"
                                onClick={() => handleDiscard(piece.id)}
                                title="Descartar"
                              >
                                🗑️
                              </button>
                            )}
                          </>
                        )}
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleOpenModal(piece)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        {piece.status === 'DESCARTADO' && (
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleDelete(piece.id)}
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={filteredPieces.length}
          />
        </>
      )}

      {/* Modal de crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingPiece ? 'Editar Pieza en Desarrollo' : 'Nueva Pieza en Desarrollo'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} data-modal-default="true">
              {editingPiece ? 'Actualizar' : 'Crear'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label">Tipo de Pieza *</label>
            <select
              className="input"
              value={pieceType}
              onChange={(e) => {
                const newType = e.target.value as 'P' | 'A' | 'C' | 'N';
                setPieceType(newType);
                fetchSuggestedCode(newType, 'PLATA');
              }}
              disabled={!!editingPiece}
              required
            >
              <option value="P">Pulsera (P)</option>
              <option value="A">Anillo (A)</option>
              <option value="C">Colgante (C)</option>
              <option value="N">Necklace (N)</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Código de Pieza *</label>
            <input
              type="text"
              className="input"
              value={formData.piece_code}
              onChange={(e) => setFormData({ ...formData, piece_code: e.target.value })}
              required
              disabled={!!editingPiece}
              placeholder={suggestedCode || undefined}
            />
            {!editingPiece && suggestedCode && (
              <p style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Sugerencia: <strong>{suggestedCode}</strong>{' '}
                {loadingSuggestion && ' (calculando...)'}
              </p>
            )}
          </div>

          <div className="input-group">
            <label className="input-label">Nombre *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="input-group">
            <label className="input-label">Descripción</label>
            <textarea
              className="input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="input-group">
            <label className="input-label">Prioridad *</label>
            <select
              className="input"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as DevelopmentPiece['priority'] })}
              required
            >
              <option value="BAJA">Baja</option>
              <option value="MEDIA">Media</option>
              <option value="ALTA">Alta</option>
            </select>
          </div>

          <div className="input-group">
            <label className="input-label">Proveedor Fundición</label>
            <input
              type="text"
              className="input"
              value={formData.foundry_supplier}
              onChange={(e) => setFormData({ ...formData, foundry_supplier: e.target.value })}
              placeholder="Ej: Fundición Express"
            />
          </div>

          {editingPiece && (
            <div className="input-group">
              <label className="input-label">Estado</label>
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as DevelopmentPiece['status'] })}
              >
                <option value="SIN_ENVIAR">Sin Enviar</option>
                <option value="ENVIADO">Enviado</option>
                <option value="REVISION">Revisión</option>
                <option value="PRODUCCION">Producción</option>
                <option value="DESCARTADO">Descartado</option>
              </select>
            </div>
          )}
        </form>
      </Modal>

      {/* Modal de detalle */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedPiece(null);
        }}
        title={`Detalle: ${selectedPiece?.piece_code}`}
        footer={
          <button className="btn btn-secondary" onClick={() => setIsDetailModalOpen(false)}>
            Cerrar
          </button>
        }
      >
        {selectedPiece && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <strong>Código:</strong> {selectedPiece.piece_code}
            </div>
            <div>
              <strong>Tipo:</strong> {PIECE_TYPE_LABELS[selectedPiece.piece_type]}
            </div>
            <div>
              <strong>Nombre:</strong> {selectedPiece.name}
            </div>
            <div>
              <strong>Prioridad:</strong>{' '}
              <span
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: PRIORITY_COLORS[selectedPiece.priority],
                  backgroundColor: `${PRIORITY_COLORS[selectedPiece.priority]}20`,
                }}
              >
                {PRIORITY_LABELS[selectedPiece.priority]}
              </span>
            </div>
            {selectedPiece.foundry_supplier && (
              <div>
                <strong>Proveedor:</strong> {selectedPiece.foundry_supplier}
              </div>
            )}
            {selectedPiece.description && (
              <div>
                <strong>Descripción:</strong> {selectedPiece.description}
              </div>
            )}
            <div>
              <strong>Estado:</strong>{' '}
              <span
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: STATUS_COLORS[selectedPiece.status],
                  backgroundColor: `${STATUS_COLORS[selectedPiece.status]}20`,
                }}
              >
                {STATUS_LABELS[selectedPiece.status]}
              </span>
            </div>
            {selectedPiece.foundry_notes && (
              <div>
                <strong>Notas Fundidor:</strong> {selectedPiece.foundry_notes}
              </div>
            )}
            {selectedPiece.revision_notes && (
              <div>
                <strong>Notas Revisión:</strong> {selectedPiece.revision_notes}
              </div>
            )}
            {selectedPiece.sent_to_foundry_at && (
              <div>
                <strong>Enviado a Fundidor:</strong>{' '}
                {new Date(selectedPiece.sent_to_foundry_at).toLocaleString('es-ES')}
              </div>
            )}

            <div>
              <strong>Checklist de Aprobación:</strong>
              <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  'Diseño 3D verificado',
                  'Prototipo aprobado',
                  'Costes calculados',
                  'Proveedor confirmado'
                ].map((task) => {
                  const item = selectedPiece.approval_checklist?.find(i => i.task === task);
                  const isCompleted = item?.completed || false;

                  return (
                    <label key={task} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={async (e) => {
                          const newChecklist = [...(selectedPiece.approval_checklist || [])];
                          const index = newChecklist.findIndex(i => i.task === task);
                          if (index >= 0) {
                            newChecklist[index].completed = e.target.checked;
                          } else {
                            newChecklist.push({ task, completed: e.target.checked });
                          }

                          try {
                            const updated = await api.put(`/development-pieces/${selectedPiece.id}`, {
                              approval_checklist: newChecklist
                            });
                            setSelectedPiece(updated.data);
                            loadPieces();
                          } catch (err) {
                            showError('Error al actualizar checklist');
                          }
                        }}
                      />
                      <span style={{ textDecoration: isCompleted ? 'line-through' : 'none', color: isCompleted ? 'var(--text-secondary)' : 'inherit' }}>
                        {task}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <strong>Archivos ({selectedPiece.files.length}):</strong>
              {selectedPiece.files.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No hay archivos</p>
              ) : (
                <div style={{ marginTop: '0.5rem' }}>
                  {selectedPiece.files
                    .sort((a, b) => b.version - a.version)
                    .map((file) => (
                      <div
                        key={file.id}
                        style={{
                          padding: '0.5rem',
                          marginBottom: '0.5rem',
                          backgroundColor: 'var(--bg-secondary)',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div>
                            <strong>v{String(file.version).padStart(3, '0')}</strong> - {file.drive_file_name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {file.drive_folder} - {new Date(file.uploaded_at).toLocaleDateString('es-ES')}
                          </div>
                          {file.description && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                              {file.description}
                            </div>
                          )}
                        </div>
                        <a
                          href={file.drive_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-small btn-primary"
                          style={{ textDecoration: 'none' }}
                        >
                          Abrir
                        </a>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de subir archivo */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setUploadFile(null);
          setUploadDescription('');
        }}
        title={`Subir Archivo: ${selectedPiece?.piece_code}`}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsUploadModalOpen(false);
                setUploadFile(null);
                setUploadDescription('');
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUploadFile}
              disabled={!uploadFile || uploading}
              data-modal-default="true"
            >
              {uploading ? 'Subiendo...' : 'Subir'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="input-group">
            <label className="input-label">Archivo 3D *</label>
            <input
              type="file"
              accept=".3dm,.step,.stp,.obj,.stl"
              className="input"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
            <p style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              Formatos soportados: .3dm, .step, .stp, .obj, .stl
            </p>
          </div>
          <div className="input-group">
            <label className="input-label">Descripción (opcional)</label>
            <textarea
              className="input"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              rows={2}
              placeholder="Ej: Master, Revisión, etc."
            />
          </div>
        </div>
      </Modal>

      {/* Modal de mover a producción */}
      <Modal
        isOpen={isMoveToProductionModalOpen}
        onClose={() => {
          setIsMoveToProductionModalOpen(false);
          setSelectedPiece(null);
        }}
        title={`Mover a Producción: ${selectedPiece?.piece_code}`}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsMoveToProductionModalOpen(false);
                setSelectedPiece(null);
              }}
            >
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleMoveToProduction} data-modal-default="true">
              Mover a Producción
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ color: 'var(--text-secondary)' }}>
            Los archivos se moverán de DISEÑO a FINALES en Google Drive. Opcionalmente puedes crear la pieza en el
            inventario:
          </p>
          <div className="input-group">
            <label className="input-label">Material Base</label>
            <select
              className="input"
              value={moveToProductionData.material_base}
              onChange={(e) =>
                setMoveToProductionData({
                  ...moveToProductionData,
                  material_base: e.target.value as 'PLATA' | 'LATON',
                })
              }
            >
              <option value="PLATA">Plata</option>
              <option value="LATON">Latón</option>
            </select>
          </div>
          {moveToProductionData.material_base === 'PLATA' && (
            <div className="input-group">
              <label className="input-label">Peso Plata (g)</label>
              <input
                type="number"
                step="0.001"
                className="input"
                value={moveToProductionData.peso_plata}
                onChange={(e) => setMoveToProductionData({ ...moveToProductionData, peso_plata: e.target.value })}
              />
            </div>
          )}
          {moveToProductionData.material_base === 'LATON' && (
            <div className="input-group">
              <label className="input-label">Peso Latón (g)</label>
              <input
                type="number"
                step="0.001"
                className="input"
                value={moveToProductionData.peso_laton}
                onChange={(e) => setMoveToProductionData({ ...moveToProductionData, peso_laton: e.target.value })}
              />
            </div>
          )}
          <div className="input-group">
            <label className="input-label">Coste Fundición (€)</label>
            <input
              type="number"
              step="0.01"
              className="input"
              value={moveToProductionData.coste_fundicion}
              onChange={(e) =>
                setMoveToProductionData({ ...moveToProductionData, coste_fundicion: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Coste PVD (€)</label>
            <input
              type="number"
              step="0.01"
              className="input"
              value={moveToProductionData.coste_pvd}
              onChange={(e) => setMoveToProductionData({ ...moveToProductionData, coste_pvd: e.target.value })}
            />
          </div>
          <div className="input-group">
            <label className="input-label">Coste DLC (€)</label>
            <input
              type="number"
              step="0.01"
              className="input"
              value={moveToProductionData.coste_dlc}
              onChange={(e) => setMoveToProductionData({ ...moveToProductionData, coste_dlc: e.target.value })}
            />
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            💡 Si dejas los campos vacíos, solo se moverán los archivos a FINALES sin crear la pieza en inventario.
          </p>
        </div>
      </Modal>
    </div>
  );
}

